/**
 * QuickScore — no-login, non-persisted scoring session.
 *
 * Reuses the existing match-scoring stack (sport scoreboards, score
 * validation, score announcer, match timer) entirely client-side: nothing
 * here is sent to the backend or saved beyond the current browser tab.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import ConfirmModal from "../ui/ConfirmModal";
import LiveScoreboard from "../match/live/components/LiveScoreboard";
import ScoreFullscreen from "../match/live/components/ScoreFullscreen";
import "./QuickScore.css";
import useScoreAnnouncer from "../match/live/hooks/useScoreAnnouncer";
import useLiveMatchTimer from "../match/live/hooks/useLiveMatchTimer";
import { validateScoreIncrease } from "../match/live/utils/scoreValidationUtils";
import { sportManagesOwnGames } from "../../utils/sportRules";
import { useAuth } from "../../contexts/AuthContext";
import { getAllSports, getUserSports } from "../../services/api/sportsService";
import {
  getSportIcon,
  sportSupportsSets,
  getSetOptions,
  buildQuickMatch,
  validateQuickScoreSetup,
  resolveQuickScoreSummary,
} from "../../utils/quickScoreUtils";

const EMPTY_SCORE = { participant1: 0, participant2: 0 };

const QuickScore = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // ── Sport list — logged-in users only see the sports on their profile;
  // guests (no login) get the full public catalog of active sports. ───────
  const [sports, setSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(true);
  const [sportsError, setSportsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadSports = isAuthenticated ? getUserSports() : getAllSports();
    loadSports
      .then((res) => {
        if (cancelled) return;
        const list = (res?.data || []).filter((s) => s.isActive !== false);
        setSports(list);
        if (isAuthenticated && list.length === 0) {
          setSportsError("You haven't added any sports yet. Add one in Manage Sports to use Quick Score.");
        }
      })
      .catch(() => {
        if (!cancelled) setSportsError("Couldn't load sports. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setSportsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // ── Setup form state ─────────────────────────────────────────────────────
  const [step, setStep] = useState("setup"); // 'setup' | 'scoring' | 'complete'
  const [sportName, setSportName] = useState("");
  const [team1Name, setTeam1Name] = useState("");
  const [team2Name, setTeam2Name] = useState("");
  const [sets, setSets] = useState(1);
  const [formError, setFormError] = useState(null);

  // Sets selector always starts at 1 (single set/game) regardless of sport —
  // the user picks Best of 3/5 manually if they want a longer match.

  // ── Scoring state ────────────────────────────────────────────────────────
  const [quickMatch, setQuickMatch] = useState(null);
  const [currentScore, setCurrentScore] = useState(EMPTY_SCORE);
  const [matchSummary, setMatchSummary] = useState(null);
  const [infoModal, setInfoModal] = useState({ isOpen: false, title: "", message: "" });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  const { muted: announcerMuted, announce, repeat: announcerRepeat, toggleMute: announcerToggleMute } = useScoreAnnouncer();
  const {
    matchDuration,
    isTimeoutActive,
    setIsTimeoutActive,
    stopTimer,
    resetTimer,
  } = useLiveMatchTimer({ match: quickMatch });

  const closeConfirm = () => setConfirmModal({ isOpen: false });
  const closeInfo = () => setInfoModal({ isOpen: false, title: "", message: "" });

  const announceScoreChange = useCallback((participant, delta, isCorrection) => {
    const opponent = participant === "participant1" ? "participant2" : "participant1";
    const newScore = Math.max(0, (currentScore[participant] || 0) + delta);
    announce(newScore, currentScore[opponent] || 0, isCorrection);
  }, [currentScore, announce]);

  // ── Setup → start scoring ─────────────────────────────────────────────────
  const handleStart = () => {
    const error = validateQuickScoreSetup({ sportName, team1Name, team2Name });
    if (error) {
      setFormError(error);
      return;
    }
    setFormError(null);
    setQuickMatch(buildQuickMatch({ sportName, team1Name: team1Name.trim(), team2Name: team2Name.trim(), sets }));
    setCurrentScore(EMPTY_SCORE);
    setMatchSummary(null);
    setStep("scoring");
  };

  // ── Complete match ────────────────────────────────────────────────────────
  const handleCompleteMatch = useCallback((completionData) => {
    setMatchSummary(resolveQuickScoreSummary({
      completionData,
      currentScore,
      team1Name: quickMatch?.participant_1_name,
      team2Name: quickMatch?.participant_2_name,
    }));
    setQuickMatch((m) => (m ? { ...m, status: "completed" } : m));
    stopTimer();
    setStep("complete");
  }, [currentScore, quickMatch, stopTimer]);

  const handleCompleteClick = (completionData) => {
    // Volleyball/Badminton already ask the user to confirm via their own
    // "Complete Set/Match" banner button — completionData being present means
    // that confirmation already happened. Other sports show a raw "Complete"
    // header button, so confirm here to avoid an accidental tap ending the game.
    if (completionData) {
      handleCompleteMatch(completionData);
      return;
    }
    setConfirmModal({
      isOpen: true,
      title: "🏁 Complete Match",
      message: `Finish the match with the current score (${currentScore.participant1}–${currentScore.participant2})?`,
      confirmText: "Complete",
      cancelText: "Cancel",
      onConfirm: () => { closeConfirm(); handleCompleteMatch(); },
      onCancel: closeConfirm,
    });
  };

  // ── Score increase/decrease ───────────────────────────────────────────────
  const handleScoreIncrease = useCallback((participant, points = 1) => {
    if (!quickMatch) return;
    const gameRules = quickMatch.matchConfig.game_rules || {};

    // Multi-set sports (volleyball/badminton/table tennis) manage their own
    // set transitions and win detection internally (single set included) —
    // just apply the raw score change.
    if ((gameRules.total_games ?? 1) > 1 || sportManagesOwnGames(quickMatch.sport_name)) {
      announceScoreChange(participant, points, false);
      setCurrentScore((prev) => ({ ...prev, [participant]: (prev[participant] || 0) + points }));
      return;
    }

    const validation = validateScoreIncrease(participant, currentScore, quickMatch);

    if (validation?.type === "max_blocked") {
      setInfoModal({ isOpen: true, title: "🔝 Maximum Score Reached", message: validation.message });
      return;
    }

    announceScoreChange(participant, points, false);
    setCurrentScore((prev) => ({ ...prev, [participant]: (prev[participant] || 0) + points }));

    if (validation?.type === "win" || validation?.type === "cap_no_margin") {
      // Build completionData from the validation result itself (newScore /
      // opponentScore) rather than relying on the not-yet-re-rendered
      // currentScore state, which would still hold the pre-increment value.
      const completionData = {
        winnerName: validation.winnerName,
        participant1Score: participant === "participant1" ? validation.newScore : validation.opponentScore,
        participant2Score: participant === "participant2" ? validation.newScore : validation.opponentScore,
      };
      setConfirmModal({
        isOpen: true,
        title: validation.type === "cap_no_margin" ? "🔝 Score Cap Reached" : "🏆 Win Condition Met!",
        message: validation.message,
        confirmText: "Complete Match",
        cancelText: "Keep Playing",
        onConfirm: () => { closeConfirm(); handleCompleteMatch(completionData); },
        onCancel: closeConfirm,
      });
    }
  }, [quickMatch, currentScore, announceScoreChange, handleCompleteMatch]);

  const handleScoreDecrease = useCallback((participant, points = 1) => {
    announceScoreChange(participant, -points, true);
    setCurrentScore((prev) => ({ ...prev, [participant]: Math.max(0, (prev[participant] || 0) - points) }));
  }, [announceScoreChange]);

  const handleResetScoreRaw = useCallback(() => {
    setCurrentScore(EMPTY_SCORE);
    resetTimer();
  }, [resetTimer]);

  const handleResetScoreClick = () => {
    setConfirmModal({
      isOpen: true,
      title: "⚠️ Reset Score",
      message: "Reset the score to 0–0? This cannot be undone.",
      confirmText: "Reset",
      cancelText: "Cancel",
      onConfirm: () => { closeConfirm(); handleResetScoreRaw(); },
      onCancel: closeConfirm,
    });
  };

  const handleToggleTimeout = () => {
    if (isTimeoutActive) {
      setIsTimeoutActive(false);
    } else {
      setIsTimeoutActive(true);
      stopTimer();
    }
  };

  // ── Play again / new setup / exit ─────────────────────────────────────────
  const handlePlayAgain = () => {
    setQuickMatch(buildQuickMatch({
      sportName,
      team1Name: team1Name.trim(),
      team2Name: team2Name.trim(),
      sets,
    }));
    setCurrentScore(EMPTY_SCORE);
    setMatchSummary(null);
    setStep("scoring");
  };

  const handleNewSetup = () => {
    setQuickMatch(null);
    setCurrentScore(EMPTY_SCORE);
    setMatchSummary(null);
    setStep("setup");
  };

  const handleExit = () => {
    navigate(isAuthenticated ? "/dashboard" : "/login");
  };

  return (
    <GameLayout showFloatingNav={false} className="quickscore-page">
      <div className="game-background" style={{ overflowY: "auto", padding: "1.5rem 1rem" }}>
        {/* Scoring screen gets a wider column so the big score/team-name/timer
            layout can use the available desktop width instead of being
            squeezed into a narrow form-width column. */}
        <div style={{ maxWidth: step === "scoring" ? "1100px" : "720px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
            <button
              onClick={handleExit}
              style={{
                background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.15)",
                color: "#e2e8f0", borderRadius: "0.5rem", padding: "0.4rem 0.9rem", cursor: "pointer",
              }}
            >
              ← Exit
            </button>
            <h2 style={{ color: "#fff", margin: 0 }}>⚡ Quick Score</h2>
          </div>

          {step === "setup" && (
            <FloatingCard className="form-section-gaming" size="large">
              <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem", marginTop: "-0.5rem" }}>
                {isAuthenticated
                  ? "Pick one of your sports, name your teams, and start scoring."
                  : "No login needed — pick a sport, name your teams, and start scoring."}
              </p>

              <div className="form-group-gaming">
                <label className="form-label-gaming" htmlFor="qs-sport">Sport *</label>
                <select
                  id="qs-sport"
                  className="form-select-gaming"
                  value={sportName}
                  onChange={(e) => setSportName(e.target.value)}
                  disabled={sportsLoading}
                >
                  <option value="">{sportsLoading ? "Loading sports…" : "Select a sport…"}</option>
                  {sports.map((s) => (
                    <option key={s.id} value={s.name}>
                      {getSportIcon(s.name)} {s.name}
                    </option>
                  ))}
                </select>
                {sportsError && (
                  <div style={{ color: "#f87171", fontSize: "0.8rem", marginTop: "0.35rem" }}>
                    {sportsError}
                    {isAuthenticated && sports.length === 0 && (
                      <> <button
                        type="button"
                        onClick={() => navigate("/sports", { state: { from: "quick-score" } })}
                        style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                      >
                        Manage Sports
                      </button></>
                    )}
                  </div>
                )}
              </div>

              <div className="form-row-gaming">
                <div className="form-group-gaming">
                  <label className="form-label-gaming" htmlFor="qs-team1">Team / Player 1 *</label>
                  <input
                    id="qs-team1"
                    className="form-input-gaming"
                    value={team1Name}
                    maxLength={40}
                    placeholder="e.g. Team A"
                    onChange={(e) => setTeam1Name(e.target.value)}
                  />
                </div>
                <div className="form-group-gaming">
                  <label className="form-label-gaming" htmlFor="qs-team2">Team / Player 2 *</label>
                  <input
                    id="qs-team2"
                    className="form-input-gaming"
                    value={team2Name}
                    maxLength={40}
                    placeholder="e.g. Team B"
                    onChange={(e) => setTeam2Name(e.target.value)}
                  />
                </div>
              </div>

              {sportSupportsSets(sportName) && (
                <div className="form-group-gaming">
                  <label className="form-label-gaming" htmlFor="qs-sets">
                    Best of <span className="form-label-optional">(sets)</span>
                  </label>
                  <select
                    id="qs-sets"
                    className="form-select-gaming"
                    value={sets}
                    onChange={(e) => setSets(parseInt(e.target.value, 10))}
                  >
                    {getSetOptions(sportName).map((n) => (
                      <option key={n} value={n}>{n === 1 ? "1 Set" : `Best of ${n}`}</option>
                    ))}
                  </select>
                </div>
              )}

              {formError && (
                <div style={{ color: "#f87171", fontSize: "0.85rem", margin: "0.5rem 0" }}>{formError}</div>
              )}

              <div style={{ marginTop: "1rem" }}>
                <FloatingButton variant="success" size="large" onClick={handleStart}>
                  🏁 Start Scoring
                </FloatingButton>
              </div>
            </FloatingCard>
          )}

          {step === "scoring" && quickMatch && (
            <ScoreFullscreen enabled={!/basketball/i.test(quickMatch.sport_name || "")}>
            <LiveScoreboard
              match={quickMatch}
              currentScore={currentScore}
              isReadOnlyMode={false}
              // NOTE: isMatchActive must stay independent of isTimeoutActive —
              // scoreboards gate their whole quick-controls row (including the
              // Timeout/Resume toggle) behind isMatchActive, so ANDing it with
              // !isTimeoutActive would hide the only way to end a timeout.
              isMatchActive={quickMatch.status !== "completed"}
              onScoreIncrease={handleScoreIncrease}
              onScoreDecrease={handleScoreDecrease}
              onResetScore={handleResetScoreClick}
              onResetSetScore={handleResetScoreRaw}
              onToggleTimeout={handleToggleTimeout}
              onCompleteMatch={handleCompleteClick}
              matchDuration={matchDuration}
              isTimeoutActive={isTimeoutActive}
              announcerMuted={announcerMuted}
              onAnnouncerToggleMute={announcerToggleMute}
              onAnnouncerRepeat={announcerRepeat}
            />
            </ScoreFullscreen>
          )}

          {step === "complete" && matchSummary && (
            <FloatingCard className="form-section-gaming" size="large" style={{ textAlign: "center" }}>
              <h2 style={{ color: "#fff" }}>
                {matchSummary.isDraw ? "🤝 It's a Draw!" : `🏆 ${matchSummary.winnerName} Wins!`}
              </h2>
              <p style={{ fontSize: "2.25rem", fontWeight: 800, color: "#e2e8f0", margin: "0.5rem 0" }}>
                {matchSummary.score1} – {matchSummary.score2}
              </p>
              {matchSummary.sets && (
                <p style={{ color: "rgba(255,255,255,0.65)" }}>
                  Sets: {matchSummary.sets.map((s) => `${s.p1}-${s.p2}`).join(", ")}
                </p>
              )}
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap", marginTop: "1.5rem" }}>
                <FloatingButton variant="primary" onClick={handlePlayAgain}>🔄 Play Again</FloatingButton>
                <FloatingButton variant="secondary" onClick={handleNewSetup}>⚙️ New Setup</FloatingButton>
                <FloatingButton variant="info" onClick={handleExit}>🚪 Exit</FloatingButton>
              </div>
            </FloatingCard>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
      />
      <ConfirmModal
        isOpen={infoModal.isOpen}
        title={infoModal.title}
        message={infoModal.message}
        confirmText="OK"
        hideCancel
        onConfirm={closeInfo}
        onCancel={closeInfo}
      />
    </GameLayout>
  );
};

export default QuickScore;
